import { get, isEmpty } from 'lodash';
import dayjs from '@/localPlugins/dayjs';

// var m1 = moment;
// var m2 = new moment();
// m2.updateLocale(ack);

// debugger;

const hourFormat = 'h[h]';
const dayFormat = 'd[d]';
const minuteFormat = 'm[m]';
const secondFormat = 's[s]';
const msFormat = 'SSS[ms]';
// Formats can be found here https://day.js.org/docs/en/display/format


const TEN = 10;
const FULL_TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const MILLISECONDS_IN_SECOND = 1000;

export const isIsoDatetimeWithOffset = (value) => {
  if (typeof value !== 'string') return false;

  const parsed = dayjs(value);

  return parsed.isValid() && /Z|[+-]\d{2}:?\d{2}$/.test(value);
};

const buildDurationFormat = (duration, allowMilliSeconds = false) => {
  const formatParts = [];
  if (duration.days()) {
    formatParts.push(dayFormat);
  }
  if (duration.hours()) {
    formatParts.push(hourFormat);
  }
  if (duration.minutes()) {
    formatParts.push(minuteFormat);
  }
  if (duration.seconds()) {
    formatParts.push(secondFormat);
  }

  if (duration.milliseconds() && allowMilliSeconds && duration.seconds() < 1) {
    formatParts.push(msFormat);
  }

  // default display
  if (!formatParts.length) {
    formatParts.push(secondFormat);
  }

  return formatParts.join(' ');
};

export const MAX_HOURS_FOR_DATE = 23;
export const MAX_MINUTES_FOR_DATE = 59;
export const durationFormatter = (seconds = 0, allowMilliSeconds = false) => {
  const duration = dayjs.duration(seconds, 'seconds');
  const format = buildDurationFormat(duration, allowMilliSeconds);
  return dayjs.utc(duration.asMilliseconds()).format(format);
};

export const humanizeDuration = (seconds = 0) => dayjs.duration(seconds, 'seconds').humanize().replace(/a |an /, '');

export const getHoursAndMinutes = (dateObj) => {
  if (!dateObj) {
    return { hours: 0, minutes: 0};
  }
  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  return { hours, minutes };
};

export const setHoursAndMinutes = (dateObj, hours, minutes) => {
  if (!dateObj) {
    return new Date();
  }
  if (hours >= 0) {
    dateObj.setHours(hours);
  }
  if (minutes >= 0) {
    dateObj.setMinutes(minutes);
  }
  return dateObj;
};
export const getHoursMinutesString = (hours, minutes) => {
  const hoursStr = hours < TEN ? `0${hours}` : hours;
  const minutesStr = minutes < TEN ? `0${minutes}` : minutes;
  return `${hoursStr}:${minutesStr}`;
};

export const getTimeZoneListWithAbbr = () => {
  const allTimeZones = Intl.supportedValuesOf('timeZone');
  const formattedTimeZones = allTimeZones.map(zone => `${zone} (${dayjs().tz(zone).format('z')})`);

  return formattedTimeZones;
};

/**
 * Get the abbreviation of a given time zone.
 *
 * @param {string} zone - The time zone identifier (e.g., 'America/New_York').
 * @returns {string} - The abbreviation of the specified time zone.
 */
export const getTimeZoneAbbr = (zone) => {
  return dayjs().tz(zone).format('z');
};

/**
 * Get the general timezone abbreviation for metrics (without daylight savings distinction).
 * Returns abbreviations like 'ET' instead of 'EST'/'EDT', 'CT' instead of 'CST'/'CDT'.
 *
 * @param {string} zone - The time zone identifier (e.g., 'America/New_York').
 * @returns {string} - The general abbreviation of the specified time zone.
 */
export const getMetricsTimeZoneAbbr = (zone) => {
  const timezoneMapping = {
    'America/New_York': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Phoenix': 'MT',
    'America/Los_Angeles': 'PT',
    'America/Anchorage': 'AKT',
    'Pacific/Honolulu': 'HST',
    'Canada/Eastern': 'ET',
    'Canada/Central': 'CT',
    'Canada/Mountain': 'MT',
    'Canada/Pacific': 'PT',
    'US/Eastern': 'ET',
    'US/Central': 'CT',
    'US/Mountain': 'MT',
    'US/Pacific': 'PT',
    'US/Alaska': 'AKT',
    'US/Hawaii': 'HST',
  };

  return timezoneMapping[zone] || dayjs().tz(zone).format('z');
};

/**
 * Determines the time zone to use based on configuration.
 *
 * @param {string} browserTimeZone - The browser's detected time zone.
 * @param {Object} dispatch - The Vuex dispatch instance.
 * @returns {string} - The time zone to use.
 */
export const useTimeZone = async(browserTimeZone, dispatch, configState) => {
  //get data from state to avoid redundant api calls
  //this could server as a simple caching mechanism
  const timeZoneState = get(configState, 'timezone.input', '');

  if (!isEmpty(timeZoneState)) {
    return timeZoneState;
  }

  try {
    // Fetch the time zone configuration from the store
    const response = await dispatch('configs/getTimeZoneConfig');
    const timezone = get(response, 'input', '');

    // Return the configured timezone if available, otherwise fallback to browser timezone
    return isEmpty(timezone) ? browserTimeZone : timezone;
  } catch (error) {
    // Fallback to browser timezone in case of any error fetching from server
    return browserTimeZone;
  }
};


/**
 * Formats a given date according to a specified timezone and format.
 *
 * @param {Date|string} date - The date to format, either as a Date object or a string.
 * @param {string} timeZoneState - The timezone identifier (e.g., 'America/Los_Angeles').
 * @param {string} format - The format string for the output date (e.g., 'YYYY-MM-DD').
 * @returns {string} - The formatted date string according to the given timezone and format.
 */

export const formatDateWithTimezone = (date, timeZoneState, format) => {
  return dayjs(date).tz(timeZoneState).format(format);
};


export const unixStampToTzConvertedMoment = (unixstamp, tzName) => {
  return dayjs.unix(unixstamp).tz(tzName);
};

export const unixStampToTzAttachedMoment = (unixStamp, tzName) => {
  const formatted = dayjs.unix(unixStamp).format(FULL_TIMESTAMP_FORMAT);
  return dayjs.tz(formatted, FULL_TIMESTAMP_FORMAT, tzName);
};

export const getTodayDate = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

export const convert24to12hours = (timestamp) => {
  /* converts timestamp to 12 hour format using today as date
  timestamp format: 2021-03-22 13:06:00
  return format: 01:06PM
  timestamp format: 2021-03-22 01:06
  return format: 01:06AM
  timestamp format: 2021-03-22 01:06 PM
  return format: 01:06PM
  timestamp format: 2021-03-22 01:06PM
  return format: ''
  timestamp format: 2021-03-22 13:06PM
  return format: ''
  */
  if (!timestamp) { return '' }
  const t = dayjs(`${getTodayDate()} ${timestamp}`, 'YYYY-MM-DD HH:mm');
  if (!t.isValid()) { return '' }
  return t.format('hh:mmA');
};

/**
 * Generates the default start and end times for aiq-date-picker when using type="dateRange".
 *
 * By default, the component sets both start and end times to 00:00:00.
 * In most cases, we want:
 *   - Start time  → 00:00:00
 *   - End time    → 23:59:59
 *
 * The `default-time` prop only uses the time portion of the provided Date objects.
 * The actual date value is irrelevant, so we generate Date instances using today's
 * date purely as a clean and consistent placeholder.
 */
export const getDatePickerDefaultTime = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const today = currentDate.getDate();
  return [
    new Date(currentYear, currentMonth, today, 0, 0, 0),
    new Date(currentYear, currentMonth, today, 23, 59, 59), // eslint-disable-line no-magic-numbers
  ];
};

